import moment from 'moment';

export const GROUP_BY = {
  RECIPIENT_ONLY: 'RECIPIENT_ONLY',
  RECIPIENT: 'RECIPIENT',
  ENTRY_DATE: 'ENTRY_DATE',
  NONE: 'NONE',
};

// Tri du puits : par ordre d'arrivée des BL ou par date d'entrée dans le puits
export const SORT = {
  ID: '',
  CREATED_AT: 'created_at',
};

// Préfixe des commandes créées manuellement dans le TMS
const MANUAL_KEY_PREFIX = 'TMS-';

// Statuts filtrables : dans le puits, ordonnancée à une date sans camion
// (« À placer »), ou affectée à un camion
export const POOL_STATUSES = {
  PLANNED: { label: 'Disponible', color: '#00b75f' },
  SCHEDULED: { label: 'À placer', color: '#b7791f' },
  ASSIGNED: { label: 'Affecté', color: '#079992' },
};

// BL supprimé du puits depuis le TMS (onglet « Supprimés », non filtrable)
const DELETED_STATUS = { label: 'Supprimé', color: '#c0392b' };

/**
 * Statut d'une ligne : seules les lignes « Disponible » sont dans le puits
 * et peuvent être envoyées en ordonnancement
 */
export const getPoolStatusKey = (element) => {
  if (element?.is_archived) return 'DELETED';
  if (element?.status === 'PLANNED') return 'PLANNED';
  if (element?.status === 'SCHEDULED') {
    return element.associated_tour_id ? 'ASSIGNED' : 'SCHEDULED';
  }
  return element?.status;
};

/**
 * Statut d'une ligne du puits : point de couleur + libellé
 */
export const getOrderStatus = (element) =>
  (getPoolStatusKey(element) === 'DELETED' && DELETED_STATUS) ||
  POOL_STATUSES[getPoolStatusKey(element)] || {
    label: element?.status || 'Non défini',
    color: '#9aa0a6',
  };

const weightFormatter = new Intl.NumberFormat('fr-FR', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

export const formatWeight = (kg) => `${weightFormatter.format(kg || 0)} kg`;

/**
 * Poids total d'une liste d'articles (poids unitaire × quantité)
 */
export const getItemsWeight = (items = []) =>
  items.reduce(
    (sum, item) =>
      sum + (parseFloat(item.weight) || 0) * (parseInt(item.quantity, 10) || 1),
    0
  );

/**
 * Libellé et ton de la date transport : rouge si passée, ambre si absente
 */
export const getTransportDate = (date) => {
  if (!date) return { label: 'RDV non pris', tone: 'warning' };
  const value = moment(date);
  return {
    label: value.format('DD/MM/YYYY'),
    tone: value.isBefore(moment(), 'day') ? 'danger' : null,
  };
};

/**
 * Date d'entrée dans le puits (création du BL) : jour et heure, en heure
 * locale
 */
export const getEntryDate = (createdAt) => {
  if (!createdAt) return { day: null, label: '—', time: null };
  const value = moment(createdAt);
  return {
    day: value.format('YYYY-MM-DD'),
    label: value.format('DD/MM/YYYY'),
    time: value.format('HH:mm'),
  };
};

/**
 * Normalise un nom renvoyé par /tours-planning/filters (parfois encore
 * entouré de guillemets JSON)
 */
export const cleanClientName = (name) =>
  typeof name === 'string' ? name.replace(/^"(.*)"$/, '$1') : name;

/**
 * Transforme une ligne de /tours-planning?unit=BL (un réceptionnaire à
 * une date) en lignes de BL, une par delivery_detail
 */
export const toOrderGroup = (element) => {
  const endAddress = element?.raw_data?.step?.end_address || {};
  const deliveryDetails = element?.delivery_details || [];
  const groupDate = element?.delivery_date || null;

  const rows = (
    deliveryDetails.length > 0 ? deliveryDetails : [{ items: [] }]
  ).map((detail, index) => {
    const blKey = detail.delivery_key || element?.data_key || '—';
    const items = detail.items || [];
    return {
      key: `${element.id}-${detail.id || index}`,
      planningId: element.id,
      // Sans BL rattaché, la ligne se sélectionne avec son réceptionnaire
      detailId: detail.id || null,
      detail,
      blKey,
      isManual: blKey.startsWith(MANUAL_KEY_PREFIX),
      items,
      weight: getItemsWeight(items),
      date: detail.delivery_date || groupDate,
      createdAt: detail.created_at || null,
    };
  });

  const createdDates = rows.map((row) => row.createdAt).filter(Boolean);

  return {
    id: element.id,
    element,
    status: element?.status,
    recipient: endAddress.name || 'Réceptionnaire non défini',
    recipientCode: endAddress.address_key || null,
    city: [endAddress.city, endAddress.zipcode].filter(Boolean).join(' '),
    street: endAddress.street || '',
    date: groupDate,
    // Suppression depuis le TMS : date (UTC) et auteur, absents avant le suivi
    archivedAt: element?.archived_at || null,
    isDeleted: Boolean(element?.is_archived),
    // Camion prévu par le fichier : affecté d'office à l'ordonnancement
    plannedVehicle:
      `${element?.raw_data?.step?.vehicle_imat_if_any || ''}`.trim() || null,
    archivedBy: element?.archived_by_name || element?.archived_by_email || null,
    createdAt: createdDates.length
      ? createdDates.reduce((min, date) => (date < min ? date : min))
      : null,
    // BL du réceptionnaire, y compris ceux des autres pages
    totalBlCount: element?.delivery_details_count || rows.length,
    statusKey: getPoolStatusKey(element),
    isSchedulable: getPoolStatusKey(element) === 'PLANNED',
    weight: rows.reduce((sum, row) => sum + row.weight, 0),
    rows,
  };
};
